/**
 * FilevineEntityPreview - Compact preview of a Filevine collection item
 *
 * Displays fields based on the section config's mainLeftFieldID and mainRightFieldID.
 * Used in table rows and entity linking cards.
 */

import React from 'react';

import { cn } from '@/lib/utils';
import type {
  DetailedFilevineCollectionItemSchema,
  FilevineSectionConfigDataSchema,
  FilevineSectionFieldConfigSchema,
} from '@/services/api/generated/entitySyncService';

import { FieldValue } from '../utils/fieldRenderers';
import {
  getDisplayableFields,
  getFieldValue,
  sortFieldsByPosition,
} from '../utils/fieldUtils';

interface FilevineEntityPreviewProps {
  item: DetailedFilevineCollectionItemSchema;
  fieldConfigs: FilevineSectionFieldConfigSchema[];
  sectionConfigData?: FilevineSectionConfigDataSchema;
  className?: string;
}

/**
 * Select which fields to display based on section config's mainLeftFieldID and mainRightFieldID.
 * Falls back to first displayable fields by position if IDs not specified.
 */
function selectDisplayFields(
  fieldConfigs: FilevineSectionFieldConfigSchema[],
  sectionConfigData?: FilevineSectionConfigDataSchema,
): FilevineSectionFieldConfigSchema[] {
  const { mainLeftFieldID, mainRightFieldID } = sectionConfigData ?? {};

  const mainFields: FilevineSectionFieldConfigSchema[] = [];

  if (mainLeftFieldID) {
    const leftField = fieldConfigs.find(
      (fc) => fc.filevine_id === mainLeftFieldID,
    );
    if (leftField) mainFields.push(leftField);
  }

  if (mainRightFieldID) {
    const rightField = fieldConfigs.find(
      (fc) => fc.filevine_id === mainRightFieldID,
    );
    if (rightField) mainFields.push(rightField);
  }

  if (mainFields.length > 0) {
    return mainFields;
  }

  // Fallback: use first 2 displayable fields by position
  const displayable = getDisplayableFields(fieldConfigs);
  const sorted = sortFieldsByPosition(displayable);
  return sorted.slice(0, 2);
}

export const FilevineEntityPreview: React.FC<FilevineEntityPreviewProps> = ({
  item,
  fieldConfigs,
  sectionConfigData,
  className,
}) => {
  const displayFields = selectDisplayFields(fieldConfigs, sectionConfigData);

  if (displayFields.length === 0) {
    return (
      <div className={cn('text-sm text-zinc-500', className)}>
        No preview available
      </div>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      {displayFields.map((fc) => (
        <div key={fc.id}>
          <FieldValue
            value={getFieldValue(item, fc)}
            fieldType={fc.custom_field_type}
          />
        </div>
      ))}
    </div>
  );
};

export default FilevineEntityPreview;
